import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    useAddAdminTicketMessageMutation,
    useAddTicketMessageMutation,
    useDeleteTicketMutation,
    useGetAdminTicketQuery,
    useGetMyTicketQuery,
    useGetTicketStatusesQuery
} from '../../api/tickets.js';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import { Dropdown } from 'primereact/dropdown';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import { useErrorToast } from '../../hooks/useToast.jsx';
import dayjs from 'dayjs';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import BaseButton from '../../components/form/BaseButton.jsx';
import { PERMISSION_TICKETS_ADMIN, PERMISSION_TICKETS_USER } from '../../utils/constants.js';
import IconSend from '../../components/images/IconSend.jsx';
import IconAdd from '../../components/images/IconAdd.jsx';
import IconMinus from '../../components/images/IconMinus.jsx';

const getStatusColorClass = (status, type = 'bg') => {
    const colorClasses = {
        open: {
            bg: 'bg-theme1-brightRed2',
            text: 'text-theme1-brightRed2'
        },
        'in-progress': {
            bg: 'bg-theme1-warning',
            text: 'text-theme1-warning'
        },
        resolved: {
            bg: 'bg-theme1-success',
            text: 'text-theme1-success'
        }
    };

    return colorClasses[status]?.[type] || (type === 'bg' ? 'bg-gray-400' : 'text-gray-700');
};

const TicketDetail = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [newMessage, setNewMessage] = useState('');
    const [isFirstTyped, setIsFirstTyped] = useState(false);
    const [sourceScreen, setSourceScreen] = useState(false);
    const lastMessageRef = useRef(null);
    const { user, permissions: userPermissions } = useCurrentUser();

    const isFromMyTickets = location.pathname.includes('/my-tickets');
    const isFromAdminTickets = location.pathname.includes('/tickets');

    const {
        data: userTicket,
        isLoading: userTicketLoading,
        error: userTicketError
    } = useGetMyTicketQuery(id, {
        skip: !isFromMyTickets
    });

    const {
        data: adminTicket,
        isLoading: adminTicketLoading,
        error: adminTicketError
    } = useGetAdminTicketQuery(id, {
        skip: !isFromAdminTickets
    });

    const { data: ticketStatuses, isLoading: ticketStatusesLoading } = useGetTicketStatusesQuery(undefined, {
        skip: !isFromAdminTickets
    });

    const [addTicketMessage, { isLoading: addTicketMessageLoading, error: addTicketMessageError }] =
        useAddTicketMessageMutation();
    const [addAdminTicketMessage, { isLoading: addAdminTicketMessageLoading, error: addAdminTicketMessageError }] =
        useAddAdminTicketMessageMutation();

    const [deleteTicket, { isLoading: isDeleting }] = useDeleteTicketMutation();

    const ticket = useMemo(() => {
        return isFromMyTickets ? userTicket?.response?.ticket : adminTicket?.response?.ticket;
    }, [userTicket, adminTicket, isFromMyTickets]);

    const ticketStatusesList = ticketStatuses?.response?.ticket_statuses || [];

    const [selectedStatus, setSelectedStatus] = useState(ticket?.status || null);

    useEffect(() => {
        if (ticket?.status) {
            setSelectedStatus(ticket.status);
        }
    }, [ticket]);

    const scrollToLastMessage = () => {
        if (lastMessageRef.current) {
            lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    useEffect(() => {
        scrollToLastMessage();
    }, [ticket]);

    useEffect(() => {
        if (userTicketError || adminTicketError) {
            useErrorToast('An error occurred while loading the ticket details.');
        }
    }, [userTicketError, adminTicketError]);

    const handleInputChange = (e) => {
        const { value } = e.target;
        setNewMessage(value);

        if (value.trim().length > 0 && !isFirstTyped) {
            setIsFirstTyped(true);
        }

        if (value.trim().length === 0) {
            setIsFirstTyped(false);
        }
    };

    const onStatusChange = async (e) => {
        const updatedStatus = e.value;
        setSelectedStatus(updatedStatus);

        try {
            await addAdminTicketMessage({
                id,
                data: {
                    text: `Status updated to ${updatedStatus}`,
                    status: updatedStatus,
                    status_updated: true
                }
            });
        } catch (error) {
            console.error('Failed to update status:', error);
        }
    };

    const handleSendMessage = async () => {
        if (newMessage.trim()) {
            try {
                if (isFromMyTickets) {
                    await addTicketMessage({ id, data: { text: newMessage } });
                } else if (isFromAdminTickets) {
                    await addAdminTicketMessage({
                        id,
                        data: { text: newMessage, status: ticket.status }
                    });
                }

                setNewMessage('');
                setIsFirstTyped(false);
            } catch (error) {
                console.error('Failed to send message:', error);
            }
        }
    };

    const handleDeleteTicket = async () => {
        try {
            await deleteTicket(ticket.id).unwrap();
            navigate('/tickets');
        } catch (error) {
            console.error('Failed to delete user:', error);
        }
    };

    const renderTicketChat = () => {
        return ticket?.threads?.map((thread, index) => {
            const isStatusUpdate = thread.status_updated;
            const updatedStatus = isStatusUpdate ? thread.text.split(' ').pop().toLowerCase() : null;
            const capitalizedStatus = updatedStatus
                ? updatedStatus.charAt(0).toUpperCase() + updatedStatus.slice(1).replace('-', ' ')
                : null;

            return (
                <BaseCard
                    key={thread.id}
                    ref={index === ticket.threads.length - 1 ? lastMessageRef : undefined}
                    className={`m-0 mb-6 p-4 px-6 bg-theme1-white w-[90%] ${thread.user_id === user.id ? 'self-end text-theme1-text1' : 'bg-theme1-pinkPrimary text-theme1-primary'}`}
                >
                    <div className='flex justify-between gap-2 mb-1'>
                        <div className='text-base  text-theme1-primary2 leading-none mt-[3px] font-bold'>
                            {thread.user.name}
                        </div>
                        {isFromAdminTickets && thread.user_id !== user.id && (
                            <div
                                className={`text-[10px] text-white self-start rounded-md py-1 px-2 capitalize font-extrabold flex-shrink-0 ml-auto ${getStatusColorClass(ticket.status, 'bg')}`}
                            >
                                {ticket.status}
                            </div>
                        )}
                    </div>
                    <div className='text-xs text-theme1-text2 mt-1'>
                        Added on {dayjs(thread.created_at).format('DD.MM.YYYY')} at{' '}
                        {dayjs(thread.created_at).format('HH:mm')}
                    </div>
                    <div
                        className={
                            isStatusUpdate ? 'font-bold text-theme1-primary2 text-xs mt-1' : 'whitespace-pre-wrap'
                        }
                    >
                        {isStatusUpdate ? (
                            <>
                                Status updated to:{' '}
                                <span className={`capitalize ${getStatusColorClass(updatedStatus, 'text')}`}>
                                    {capitalizedStatus}
                                </span>
                            </>
                        ) : (
                            thread.text
                        )}
                    </div>
                </BaseCard>
            );
        });
    };

    const handleSourceScreen = () => {
        setSourceScreen((prev) => !prev);
    };

    return (
        <AuthLayout
            title={
                !(userTicketLoading || adminTicketLoading)
                    ? `${ticket?.type?.charAt(0).toUpperCase()}${ticket?.type?.slice(1)}`
                    : ''
            }
            backRoute={isFromMyTickets ? '/my-tickets' : '/tickets'}
            mainClassNames={'flex flex-col'}
        >
            {userTicketLoading || adminTicketLoading ? (
                <ThreeDotsLoader className='mt-6' />
            ) : (
                <div
                    className={`max-w-3xl w-full h-full flex flex-col flex-grow justify-between mx-auto py-0 ${!isFromAdminTickets ? 'pt-4' : ''}`}
                >
                    {isFromAdminTickets && (
                        <>
                            <div className='bg-theme1-tan p-4 px-8 pt-10 -mt-6 rounded-b-4xl mb-8'>
                                <div className='flex justify-between' onClick={handleSourceScreen}>
                                    <div className='text-theme1-title2 text-base font-semibold'>
                                        Source Screen{' '}
                                        <span className='text-sm text-theme1-text2 font-normal'>
                                            (click to {sourceScreen ? 'close' : 'view'})
                                        </span>
                                    </div>
                                    {!sourceScreen ? (
                                        <IconAdd className='fill-theme1-title2 w-6 h-6' />
                                    ) : (
                                        <IconMinus className='fill-theme1-title2 w-6 h-6' />
                                    )}
                                </div>
                                {sourceScreen && (
                                    <div className='bg-white rounded-full mt-4 px-4 py-2 text-sm font-bold text-theme1-info truncate'>
                                        {ticket.screen}
                                    </div>
                                )}
                            </div>
                            <div className='mb-4 px-4 flex gap-2 justify-center'>
                                {ticket.status !== 'resolved' &&
                                    (userPermissions[PERMISSION_TICKETS_ADMIN]?.update ? (
                                        <Dropdown
                                            value={selectedStatus}
                                            options={ticketStatusesList}
                                            onChange={onStatusChange}
                                            optionLabel='label'
                                            optionValue='value'
                                            placeholder={
                                                ticketStatusesLoading ? 'Loading tickets statuses...' : 'Select Status'
                                            }
                                            className='text-theme1-text1 rounded-lg border border-theme1-border1 w-full outline-0 focus:outline-0 shadow-none text-base'
                                            dropdownIcon={
                                                <svg
                                                    width='22'
                                                    height='22'
                                                    viewBox='0 0 18 18'
                                                    fill='none'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                >
                                                    <path
                                                        d='M6 12L9 15L12 12'
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                    />
                                                    <path
                                                        d='M6 6L9 3L12 6'
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                    />
                                                </svg>
                                            }
                                            collapseIcon={
                                                <svg
                                                    width='22'
                                                    height='22'
                                                    viewBox='0 0 18 18'
                                                    fill='none'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                >
                                                    <path
                                                        d='M6 12L9 15L12 12'
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                    />
                                                    <path
                                                        d='M6 6L9 3L12 6'
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                    />
                                                </svg>
                                            }
                                            disabled={ticketStatusesLoading}
                                        />
                                    ) : (
                                        <div className='text-theme1-text1 font-bold text-base'>
                                            {selectedStatus
                                                ? ticketStatusesList.find((status) => status.value === selectedStatus)
                                                      ?.label
                                                : 'No status available'}
                                        </div>
                                    ))}

                                {!!userPermissions[PERMISSION_TICKETS_ADMIN]?.delete && (
                                    <BaseButton
                                        className='bg-theme1-primary p-2 px-3 sm:px-5 text-white text-sm sm:text-base w-auto flex-shrink-0'
                                        textValue='Delete Ticket'
                                        onClick={handleDeleteTicket}
                                        disabled={isDeleting}
                                    />
                                )}
                            </div>
                        </>
                    )}

                    <div className='mt-auto flex flex-col'>
                        <div
                            className={`flex-grow flex flex-col overflow-auto ${
                                ticket.status === 'resolved'
                                    ? isFromMyTickets
                                        ? 'max-h-[700px]'
                                        : sourceScreen
                                          ? 'max-h-[550px]'
                                          : 'max-h-[600px]'
                                    : isFromMyTickets
                                      ? 'max-h-[630px]'
                                      : sourceScreen
                                        ? 'max-h-[450px]'
                                        : 'max-h-[500px]'
                            } px-4`}
                        >
                            <div className='mt-auto flex flex-col relative pb-5'>
                                {renderTicketChat()}

                                {(addTicketMessageLoading || addAdminTicketMessageLoading) && (
                                    <ThreeDotsLoader className='absolute right-0 bottom-2.5' />
                                )}

                                {(addTicketMessageError || addAdminTicketMessageError) && (
                                    <div className='text-red-500 text-sm mt-2 text-right mb-2'>
                                        Message failed to send. Please try again.
                                    </div>
                                )}
                            </div>
                        </div>
                        {ticket.status !== 'resolved' &&
                            (!!userPermissions[PERMISSION_TICKETS_USER]?.update ||
                                !!userPermissions[PERMISSION_TICKETS_ADMIN]?.update) && (
                                <div className='bg-theme1-tan rounded-t-4xl p-4 flex gap-2'>
                                    <div className='flex-grow px-5 border border-theme1-border1 rounded-4xl bg-white'>
                                        <textarea
                                            className={`w-full pt-[12px] pb-[12px] h-[49px] max-h-[66px] focus:outline-none block bg-transparent mt-[2px] mb-[2px] flex-grow ${
                                                addTicketMessageLoading || addAdminTicketMessageLoading
                                                    ? 'text-gray-400'
                                                    : ''
                                            }`}
                                            placeholder='Type a message...'
                                            value={newMessage}
                                            onChange={handleInputChange}
                                            style={{ resize: 'none' }}
                                            disabled={addTicketMessageLoading || addAdminTicketMessageLoading}
                                        />
                                    </div>
                                    <button
                                        className={`btn rounded-4xl w-[55px] h-[55px] bg-theme1-primary text-white flex items-center justify-center ${
                                            !isFirstTyped || addTicketMessageLoading || addAdminTicketMessageLoading
                                                ? 'hidden'
                                                : ''
                                        } ${!newMessage.trim() || addTicketMessageLoading || addAdminTicketMessageLoading ? 'bg-theme1-text3' : ''}`}
                                        onClick={handleSendMessage}
                                        disabled={
                                            !newMessage.trim() ||
                                            addTicketMessageLoading ||
                                            addAdminTicketMessageLoading
                                        }
                                    >
                                        <IconSend className='fill-white w-[30px] h-[30px] -ml-1' />
                                    </button>
                                </div>
                            )}
                    </div>
                </div>
            )}
        </AuthLayout>
    );
};

export default TicketDetail;
